import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tabs,
  Tab,
  Container,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import {
  WhatsApp,
  Send,
  Link as LinkIcon,
  Schedule,
  Article,
} from '@mui/icons-material';
import api from '../services/api';
import whatsappService, { WhatsappConfig, WhatsappTemplate } from '../services/whatsappService';
import spopService from '../services/spopService';
import { SPOP } from '../types';
import GroupSelectionDialog from '../components/GroupSelectionDialog';
import WhatsappConfigPanel from '../components/whatsapp/WhatsappConfigPanel';
import WhatsappQueuePanel, { QueueSettings } from '../components/whatsapp/WhatsappQueuePanel';
import WhatsappTemplatesPanel from '../components/whatsapp/WhatsappTemplatesPanel';
import WhatsappTemplateDialog from '../components/whatsapp/WhatsappTemplateDialog';
import { EMPTY_TEMPLATE, TemplateFormState } from '../constants/whatsappTemplateConstants';
import { useAuth } from '../hooks/useAuth';

interface Message {
  type: 'success' | 'error' | 'warning';
  text: string;
}

const DEFAULT_QUEUE: QueueSettings = {
  message_interval_minutes: 1,
  max_messages_per_hour: 50,
  enable_scheduling: true,
  working_hours_start: '08:00',
  working_hours_end: '18:00',
  priority_interval_minutes: { 1: 0.5, 2: 1, 3: 2, 4: 5, 5: 10 },
};

const WhatsappSettings: React.FC = () => {
  const { user } = useAuth();
  const isOperator = user?.role === 'operator';
  const operatorSpopId = isOperator && user?.spop_id ? user.spop_id : undefined;
  const [tab, setTab] = useState(isOperator ? 2 : 0);
  const [config, setConfig] = useState<WhatsappConfig>({
    instanceId: '',
    apiKey: '',
    groupId: '',
    isActive: false,
  });
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [spops, setSPOPs] = useState<SPOP[]>([]);
  const [queueSettings, setQueueSettings] = useState<QueueSettings>(DEFAULT_QUEUE);
  const [message, setMessage] = useState<Message | null>(null);
  const [loading, setLoading] = useState(false);
  const [testDialog, setTestDialog] = useState({ open: false, phone: '', message: '' });
  const [templateDialog, setTemplateDialog] = useState<{
    open: boolean;
    editMode: boolean;
    template: TemplateFormState;
  }>({
    open: false,
    editMode: false,
    template: { ...EMPTY_TEMPLATE },
  });
  const [groupDialog, setGroupDialog] = useState(false);

  useEffect(() => {
    setMessage(null);
    loadTemplates();
    loadSPOPs();
    if (!isOperator) {
      loadConfig();
      loadQueueSettings();
    }
  }, [isOperator, user?.spop_id]);

  const showMessage = (msg: Message, ms = 3000) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), ms);
  };

  const loadConfig = async () => {
    try {
      setConfig(await whatsappService.getConfig());
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      setTemplates(await whatsappService.getTemplates());
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadSPOPs = async () => {
    try {
      if (isOperator && user?.spop) {
        setSPOPs([user.spop as SPOP]);
        return;
      }
      const response = await spopService.getAllSPOPs();
      if (response.data.success) {
        setSPOPs(response.data.data.spops.filter((spop: SPOP) => spop.isActive));
      }
    } catch (error) {
      console.error('Error loading SPOPs:', error);
    }
  };

  const loadQueueSettings = async () => {
    try {
      const response = await api.get('/whatsapp/queue/settings');
      if (response.data.success) {
        setQueueSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading queue settings:', error);
    }
  };

  const saveQueueSettings = async () => {
    setLoading(true);
    try {
      const response = await api.put('/whatsapp/queue/settings', queueSettings);
      if (response.data.success) {
        showMessage({ type: 'success', text: 'Pengaturan antrian berhasil disimpan!' });
      } else {
        throw new Error(response.data.message || 'Failed to save settings');
      }
    } catch {
      showMessage({ type: 'error', text: 'Gagal menyimpan pengaturan antrian!' });
    } finally {
      setLoading(false);
    }
  };

  const processQueue = async () => {
    setLoading(true);
    try {
      const response = await api.post('/whatsapp/queue/process');
      if (response.data.success) {
        showMessage({
          type: 'success',
          text: `Antrian diproses! ${response.data.data?.processed ?? 0} pesan dikirim.`,
        });
      } else {
        showMessage({ type: 'error', text: 'Gagal memproses antrian!' });
      }
    } catch {
      showMessage({ type: 'error', text: 'Gagal memproses antrian!' });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      await whatsappService.saveConfig(config);
      showMessage({ type: 'success', text: 'Konfigurasi berhasil disimpan!' });
      loadConfig();
    } catch {
      showMessage({ type: 'error', text: 'Gagal menyimpan konfigurasi!' });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    try {
      const result = await whatsappService.testConnection();
      if (result.success) {
        showMessage({ type: 'success', text: 'Koneksi berhasil! Konfigurasi valid.' });
      } else {
        showMessage({ type: 'warning', text: 'Koneksi tersambung tapi ada masalah dengan konfigurasi.' });
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      if (errorMsg.includes('Invalid API Key') || errorMsg.includes('API Key')) {
        showMessage({ type: 'error', text: 'API Key tidak valid! Periksa konfigurasi StarSender.' }, 5000);
      } else if (errorMsg.includes('configuration not found')) {
        showMessage({ type: 'error', text: 'Konfigurasi WhatsApp belum disimpan!' }, 5000);
      } else {
        showMessage({ type: 'error', text: `Koneksi gagal: ${errorMsg}` }, 5000);
      }
    } finally {
      setLoading(false);
    }
  };

  const sendTestMessage = async () => {
    if (!config.apiKey) {
      showMessage({ type: 'error', text: 'API Key harus diisi!' });
      return;
    }
    if (!testDialog.phone.trim()) {
      showMessage({ type: 'error', text: 'Nomor WhatsApp harus diisi!' });
      return;
    }

    setLoading(true);
    try {
      await whatsappService.sendCustomMessage({
        phone: testDialog.phone,
        message: testDialog.message,
        sendToGroup: false,
        immediate: true,
      });

      let successMessage = 'Pesan test berhasil dikirim ke nomor individu!';
      let hasError = false;

      if (config.groupId?.trim()) {
        try {
          await whatsappService.sendCustomMessage({
            message: `[TEST MESSAGE]\n${testDialog.message}`,
            sendToGroup: true,
            immediate: true,
          });
          successMessage = 'Pesan test berhasil dikirim ke nomor individu dan grup!';
        } catch (groupError: any) {
          hasError = true;
          const errorMsg = groupError.response?.data?.error || groupError.message || 'Unknown error';
          successMessage = errorMsg.includes('Invalid API Key')
            ? 'Pesan dikirim ke individu, tapi API Key tidak valid untuk grup!'
            : `Pesan dikirim ke individu, gagal ke grup: ${errorMsg}`;
        }
      }

      showMessage({ type: hasError ? 'warning' : 'success', text: successMessage }, 5000);
      setTestDialog({ open: false, phone: '', message: '' });
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      showMessage(
        {
          type: 'error',
          text: errorMsg.includes('Invalid API Key')
            ? 'API Key tidak valid! Pastikan format dan nilai API Key benar di StarSender.'
            : `Gagal mengirim pesan: ${errorMsg}`,
        },
        5000
      );
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    setLoading(true);
    try {
      const t = templateDialog.template;
      const payload = {
        name: t.name,
        type: t.type,
        template: t.template,
        status: t.status || null,
        spop_id: operatorSpopId || t.spop_id || null,
        isActive: t.isActive ?? true,
      };

      if (templateDialog.editMode && t.id) {
        await whatsappService.updateTemplate(t.id, payload);
      } else {
        await whatsappService.createTemplate(payload);
      }

      showMessage({ type: 'success', text: 'Template berhasil disimpan!' });
      setTemplateDialog({ open: false, editMode: false, template: { ...EMPTY_TEMPLATE } });
      loadTemplates();
    } catch (error: any) {
      let errorMessage = 'Gagal menyimpan template!';
      if (error.response?.data?.errors) {
        errorMessage = `Validasi gagal: ${error.response.data.errors.join(', ')}`;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showMessage({ type: 'error', text: errorMessage }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id: number) => {
    if (!window.confirm('Hapus template ini?')) return;
    try {
      await whatsappService.deleteTemplate(id);
      showMessage({ type: 'success', text: 'Template berhasil dihapus!' });
      loadTemplates();
    } catch {
      showMessage({ type: 'error', text: 'Gagal menghapus template!' });
    }
  };

  const openNewTemplate = () =>
    setTemplateDialog({
      open: true,
      editMode: false,
      template: {
        ...EMPTY_TEMPLATE,
        ...(operatorSpopId ? { spop_id: operatorSpopId } : {}),
      },
    });

  const openEditTemplate = (template: WhatsappTemplate) =>
    setTemplateDialog({
      open: true,
      editMode: true,
      template: {
        id: template.id || 0,
        name: template.name,
        type: template.type,
        template: template.template,
        isActive: template.isActive ?? true,
        status: template.status || undefined,
        spop_id: template.spop_id || undefined,
      },
    });

  const activeTemplates = templates.filter((t) => t.isActive !== false).length;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <WhatsApp color="success" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {isOperator ? 'Template Pesan WhatsApp' : 'Pengaturan WhatsApp'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isOperator
              ? 'Kelola template pesan otomatis untuk SPOP Anda'
              : 'Koneksi StarSender, antrian pengiriman, dan template pesan otomatis'}
          </Typography>
          {isOperator && user?.spop && (
            <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
              SPOP: {user.spop.name} ({user.spop.areaCode})
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        {!isOperator && (
          <>
            <Chip
              icon={<LinkIcon />}
              label={config.isActive ? 'WhatsApp aktif' : 'WhatsApp nonaktif'}
              color={config.isActive ? 'success' : 'default'}
              size="small"
            />
            <Chip
              icon={<Schedule />}
              label={
                queueSettings.enable_scheduling
                  ? `Antrian ${queueSettings.working_hours_start}–${queueSettings.working_hours_end}`
                  : 'Kirim langsung (tanpa antrian)'
              }
              color={queueSettings.enable_scheduling ? 'info' : 'warning'}
              size="small"
              variant="outlined"
            />
          </>
        )}
        <Chip
          icon={<Article />}
          label={`${activeTemplates}/${templates.length} template aktif`}
          size="small"
          variant="outlined"
        />
      </Stack>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {!isOperator && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<LinkIcon />} iconPosition="start" label="Koneksi" />
            <Tab icon={<Schedule />} iconPosition="start" label="Antrian & Jadwal" />
            <Tab icon={<Article />} iconPosition="start" label="Template Pesan" />
          </Tabs>
        </Paper>
      )}

      {!isOperator && tab === 0 && (
        <WhatsappConfigPanel
          config={config}
          loading={loading}
          onChange={setConfig}
          onSave={saveConfig}
          onTestConnection={testConnection}
          onOpenTestMessage={() => setTestDialog((d) => ({ ...d, open: true }))}
          onOpenGroupPicker={() => setGroupDialog(true)}
        />
      )}

      {!isOperator && tab === 1 && (
        <WhatsappQueuePanel
          settings={queueSettings}
          loading={loading}
          onChange={setQueueSettings}
          onSave={saveQueueSettings}
          onProcessQueue={processQueue}
        />
      )}

      {(isOperator || tab === 2) && (
        <WhatsappTemplatesPanel
          templates={templates}
          spops={spops}
          operatorSpopId={operatorSpopId}
          onAdd={openNewTemplate}
          onEdit={openEditTemplate}
          onDelete={deleteTemplate}
        />
      )}

      <WhatsappTemplateDialog
        open={templateDialog.open}
        editMode={templateDialog.editMode}
        template={templateDialog.template}
        spops={spops}
        operatorSpopId={operatorSpopId}
        loading={loading}
        onChange={(template) => setTemplateDialog((d) => ({ ...d, template }))}
        onClose={() =>
          setTemplateDialog({ open: false, editMode: false, template: { ...EMPTY_TEMPLATE } })
        }
        onSave={saveTemplate}
      />

      <Dialog
        open={testDialog.open}
        onClose={() => setTestDialog((d) => ({ ...d, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Kirim pesan test</DialogTitle>
        <DialogContent dividers>
          {!config.apiKey && (
            <Alert severity="error" sx={{ mb: 2 }}>
              API Key belum dikonfigurasi. Simpan konfigurasi terlebih dahulu.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Nomor WhatsApp"
            value={testDialog.phone}
            onChange={(e) => setTestDialog((d) => ({ ...d, phone: e.target.value }))}
            margin="normal"
            required
            size="small"
            helperText="Format: 628xxxxxxxxx (tanpa +)"
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Pesan"
            value={testDialog.message}
            onChange={(e) => setTestDialog((d) => ({ ...d, message: e.target.value }))}
            margin="normal"
            required
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTestDialog((d) => ({ ...d, open: false }))}>Batal</Button>
          <Button
            onClick={sendTestMessage}
            variant="contained"
            startIcon={<Send />}
            disabled={loading || !config.apiKey || !testDialog.phone.trim()}
          >
            Kirim
          </Button>
        </DialogActions>
      </Dialog>

      <GroupSelectionDialog
        open={groupDialog}
        apiKey={config.apiKey}
        onClose={() => setGroupDialog(false)}
        onSelectGroup={(groupId: string, groupName: string) => {
          setConfig({ ...config, groupId });
          showMessage({ type: 'success', text: `Grup "${groupName}" berhasil dipilih` });
        }}
      />
    </Container>
  );
};

export default WhatsappSettings;
